import * as yup from 'yup';
import {
  createDefaultRecurringSettings,
  GROUP_AUDIENCE_LABEL,
  normalizeRecurringSettings,
} from 'src/modules/campaigns/helpers';
import { CampaignFormValues, FormMessage, PayloadMessage } from '../../types';
import {
  normalizeRecurringMessages,
  validateRecurringSettings,
} from '../../helpers/recurring';
import { buildDefaultMessages, commonDefaults } from '../shared/campaignDefaults';
import {
  hasBaseErrors,
  hasCaptureErrors,
  hasMissingGroups,
} from '../shared/primaryActionChecks';
import { commonSchemaShape } from '../shared/validationSchema';
import { CampaignTypeSpec, whenVisible } from '../policy';
import { policyIssue } from '../issues';
import { recurringSettingsFromTemplate } from 'src/modules/campaignConfigBuilder/helpers/templatePrefill';
import { blankRecurringMessage, hasErrors } from './shared';

export const recurringSpec: CampaignTypeSpec = {
  externalType: 'custom',
  scheduleKind: 'recurring',
  allowsMultipleMessages: false,
  loadsKeywords: false,
  recipients: () => ({
    label: GROUP_AUDIENCE_LABEL,
    allowDynamicGroups: true,
    initialGroupType: 'dynamic',
  }),
  keepsEmptyMessage: () => true,

  fields: config => [
    'name',
    'recipients',
    'recurringSettings',
    'messages',
    ...whenVisible(config.opt_out_message, 'optOut'),
  ],

  defaults: config => ({
    ...commonDefaults(config),
    recurringSettings: recurringSettingsFromTemplate(config.recurringSettings)
      ?? createDefaultRecurringSettings(),
    scheduledMessages: normalizeRecurringMessages<FormMessage>(
      buildDefaultMessages(config, { scheduledType: 'immediate', delay: null }),
      () => blankRecurringMessage('sms'),
    ),
  }),

  fromCampaign: (base, campaign) => ({
    ...base,
    recurringSettings: normalizeRecurringSettings(campaign.recurringSettings),
    scheduledMessages: normalizeRecurringMessages<FormMessage>(
      base.scheduledMessages,
      () => blankRecurringMessage('sms'),
    ),
  }),

  issues: (values, config) => policyIssue(
    hasBaseErrors(values, config)
    || hasCaptureErrors(values)
    || hasMissingGroups(values)
    || hasErrors(validateRecurringSettings(normalizeRecurringSettings(values.recurringSettings))),
    'recurring-primary-blocked',
  ),

  payload: ({ base, values }) => ({
    ...base,
    scheduledMessages: normalizeRecurringMessages<PayloadMessage>(
      base.scheduledMessages,
      () => ({
        type: 'sms',
        text: '',
        subject: '',
        links: [],
        position: 1,
        scheduledType: 'immediate',
        delay: null,
      }),
    ),
    groups: values.groupIds ?? [],
    recurringSettings: normalizeRecurringSettings(values.recurringSettings),
  }),

  schema: config => yup
    .object(commonSchemaShape(config, { messages: true, groups: true }))
    .test('recurring-settings-valid', 'Recurring settings are invalid', function (values) {
      const errors = validateRecurringSettings((values as CampaignFormValues).recurringSettings);
      if (!hasErrors(errors)) return true;
      return this.createError({ message: Object.values(errors)[0] });
    }),
};
